import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { TeamService } from "@/services/teamService";
import { useTenant } from "./useTenant";

// Query keys for teams
export const teamKeys = {
  all: (tid: string) => ["teams", tid] as const,
  userTeams: (tid: string, userId: string) =>
    ["teams", "user", tid, userId] as const,
  members: (tid: string) => ["teamMembers", tid] as const,
  teamMembers: (tid: string, teamId: string) =>
    ["teamMembers", tid, teamId] as const,
};

// Get all teams
export function useTeams() {
  const { tenantId } = useTenant();

  return useQuery({
    queryKey: teamKeys.all(tenantId!),
    queryFn: async () => {
      try {
        const result = await TeamService.getAllTeams(tenantId!);
        return result;
      } catch (error) {
        console.error("❌ useTeams: Query failed", error);
        throw error;
      }
    },
    enabled: !!tenantId,
    retry: 1,
    retryDelay: 1000,
  });
}

// Get user teams
export function useUserTeams(userId: string) {
  const { tenantId } = useTenant();

  return useQuery({
    queryKey: teamKeys.userTeams(tenantId!, userId),
    queryFn: () => TeamService.getUserTeams(userId, tenantId!),
    enabled: !!tenantId && !!userId,
  });
}

// Get all team members
export function useAllTeamMembers() {
  const { tenantId } = useTenant();

  return useQuery({
    queryKey: teamKeys.members(tenantId!),
    queryFn: () => TeamService.getAllTeamMembers(tenantId!),
    enabled: !!tenantId,
  });
}

// Get team members for specific team
export function useTeamMembers(teamId: string) {
  const { tenantId } = useTenant();

  return useQuery({
    queryKey: teamKeys.teamMembers(tenantId!, teamId),
    queryFn: () => TeamService.getTeamMembers(teamId, tenantId!),
    enabled: !!tenantId && !!teamId,
  });
}

// Create team
export function useCreateTeam() {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (teamData: { name: string; description?: string }) =>
      TeamService.createTeam(teamData, tenantId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.all(tenantId!) });
      queryClient.invalidateQueries({ queryKey: teamKeys.members(tenantId!) });
    },
  });
}

// Update team
export function useUpdateTeam() {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      teamId,
      teamData,
    }: {
      teamId: string;
      teamData: { name: string; description?: string };
    }) => TeamService.updateTeam(teamId, teamData, tenantId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.all(tenantId!) });
      queryClient.invalidateQueries({ queryKey: teamKeys.members(tenantId!) });
    },
  });
}

// Delete team
export function useDeleteTeam() {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (teamId: string) => TeamService.deleteTeam(teamId, tenantId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.all(tenantId!) });
      queryClient.invalidateQueries({ queryKey: teamKeys.members(tenantId!) });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

// Add team member
export function useAddTeamMember() {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      teamId,
      userId,
      role = "member",
    }: {
      teamId: string;
      userId: string;
      role?: "admin" | "member";
    }) => TeamService.addTeamMember(teamId, userId, role, tenantId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.all(tenantId!) });
      queryClient.invalidateQueries({ queryKey: teamKeys.members(tenantId!) });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

// Remove team member
export function useRemoveTeamMember() {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (teamMemberId: string) =>
      TeamService.removeTeamMember(teamMemberId, tenantId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.all(tenantId!) });
      queryClient.invalidateQueries({ queryKey: teamKeys.members(tenantId!) });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}
